import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import MemberEntryForm from '../components/memberAuth/MemberEntryForm';
import { MEMBER_ENTRY_ERRORS, buildMemberJoinFrame, validateMemberEntry } from '../components/memberAuth/memberEntry';
import { useParams } from 'react-router-dom';
import {
  fetchCommInfo,
  openCommSocket,
  type CommItem,
  type CommRoomPublic,
  type CommSocket,
  type CommStatus,
} from '../services/realtime/commClient';
import {
  RemoteNoticeOverlay,
  RemoteCaptionOverlay,
  RemoteQuestionOverlay,
  type RemoteQuestionView,
} from '../components/presentationRemote/RemotePresentationOverlays';
import { REMOTE_DESIGN_CLASS } from '../utils/remoteDesignTheme';
import BrandLogo from '../components/BrandLogo';
import { OxMark } from '../components/presentationRemote/RemotePresentationOverlays';
import { choiceAnswerPickedIndexes } from '../utils/remoteQuestion';
import { communicationChoiceSwatchStyle } from '../utils/presentationCommunicationAnswers';
import CachedAppImage from '../components/common/CachedAppImage';
import '../components/presentationRemote/RemoteControllerPanel.css';
import '../components/presentationRemote/RemoteDesignThemes.css';
import './CommStudentPage.css';

/**
 * /comm/:roomId — 학생용(로그인 없음): 명단 방은 번호 + 입장번호, 명단 없는 방은 이름 + 방 입장번호로 입장.
 * 받은 공지·질문은 대화처럼 아래로 쌓이는 기록(이 브라우저에만 저장, 방 종료·만료 시 삭제).
 * 새 메시지는 큰 팝업(프레젠테이션 카드)으로 열리고, 닫으면 작은 블록으로 남는다.
 */
type Creds = { memberId: string; token: string; nickname: string; number?: number };
type Hist = CommItem & { seen?: boolean; answer?: string; answeredAt?: number };
type HistStore = { memberId: string; expiresAt: number; items: Hist[] };

const credsKey = (roomId: string) => `cp-comm-student:${roomId}`;
const histKey = (roomId: string) => `cp-comm-student-hist:${roomId}`;
const HIST_MAX = 200;

const loadCreds = (roomId: string): Creds | null => {
  try {
    return JSON.parse(window.localStorage.getItem(credsKey(roomId)) ?? 'null') as Creds | null;
  } catch {
    return null;
  }
};
const clearLocal = (roomId: string) => {
  window.localStorage.removeItem(credsKey(roomId));
  window.localStorage.removeItem(histKey(roomId));
};

const JOIN_ERR: Record<string, string> = MEMBER_ENTRY_ERRORS;
const NoticeIcon = () => (
  <svg className="cp-comm-ico" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3 11v2a1 1 0 0 0 1 1h2l5 4V6L6 10H4a1 1 0 0 0-1 1z" />
    <path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13" />
  </svg>
);
const QuestionIcon = () => (
  <svg className="cp-comm-ico" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.6 9.3a2.5 2.5 0 0 1 4.8.9c0 1.7-2.4 2.2-2.4 3.6" />
    <path d="M12 17h.01" />
  </svg>
);
const ExpandIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M14 4h6v6M10 20H4v-6M20 4l-6.5 6.5M4 20l6.5-6.5" />
  </svg>
);

/** 학생 화면 안내: 토비서 캐릭터 + 말풍선 (튜토리얼과 같은 character_0.webp), 버튼은 말풍선 아래 */
const TobiNotice: React.FC<{
  text: string;
  tone?: 'info' | 'warn';
  compact?: boolean;
  children?: React.ReactNode;
}> = ({ text, tone = 'info', compact = false, children }) => (
  <div
    className={`cp-comm-tobi${tone === 'warn' ? ' is-warn' : ''}${compact ? ' is-compact' : ''}`}
    role={tone === 'warn' ? 'alert' : 'status'}
  >
    <p className="cp-comm-tobi-bubble">{text}</p>
    <CachedAppImage file="character_0.webp" className="cp-comm-tobi-avatar" />
    {children ? <div className="cp-comm-tobi-actions">{children}</div> : null}
  </div>
);

const hhmm = (t: number) => new Date(t).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });

/** 질문 출처 라벨 — 값이 없는 예전 항목은 표시 안 함 */
const commSourceLabel = (src?: string): string | undefined =>
  src === 'page' ? '질문 페이지' : src === 'remote' ? '프레젠테이션 리모트' : undefined;

const CommStudentPage: React.FC = () => {
  const { roomId = '' } = useParams();
  const [room, setRoom] = useState<CommRoomPublic | null>(null);
  const [phase, setPhase] = useState<'loading' | 'missing' | 'join' | 'in' | 'ended'>('loading');
  const [status, setStatus] = useState<CommStatus>('connecting');
  const [statusDetail, setStatusDetail] = useState('');
  /** 끝난 이유: 'ended'(선생님이 종료) / 'expired'·'not_found'(사용 시간 끝남·방 없음) */
  const [endReason, setEndReason] = useState('');
  const [nickname, setNickname] = useState(() => loadCreds(roomId)?.nickname ?? '');
  const [code, setCode] = useState('');
  /** 명단 입장(번호 + 개인 입장번호) 방인지 */
  const [rosterMode, setRosterMode] = useState(false);
  const [studentNo, setStudentNo] = useState(() => {
    const n = loadCreds(roomId)?.number;
    return typeof n === 'number' && n > 0 ? String(n) : '';
  });
  const [joinErr, setJoinErr] = useState('');
  const [joining, setJoining] = useState(false);
  const [memberId, setMemberId] = useState('');
  const [hist, setHist] = useState<Hist[]>([]);
  /** 이번 페이지 로드에서 받은 기록 (재연결 때 합치기용) */
  const histRef = useRef<Hist[]>([]);
  histRef.current = hist;
  const [openId, setOpenId] = useState<string | null>(null);
  /** 진행 중 자막(아직 저장 전) */
  const [liveCaption, setLiveCaption] = useState<{ id: string; text: string } | null>(null);
  const [editing, setEditing] = useState(false);
  const sockRef = useRef<CommSocket | null>(null);
  const pendingAnswerRef = useRef<{ id: string; content: string } | null>(null);
  const answerWaitRef = useRef<((ok: boolean) => void) | null>(null);
  const listEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    document.documentElement.dataset.theme = 'light';
  }, []);

  /* 이전 접속에서 이 브라우저에 남은 기록은 지움 (서버 답안은 그대로) */
  useEffect(() => {
    try {
      window.localStorage.removeItem(histKey(roomId));
    } catch {
      /* ignore */
    }
  }, [roomId]);

  useEffect(() => {
    let alive = true;
    void fetchCommInfo(roomId).then((r) => {
      if (!alive) return;
      if ('error' in r) {
        clearLocal(roomId);
        setPhase('missing');
        return;
      }
      setRoom(r);
      setRosterMode(Boolean(r.rosterMode));
      document.title = r.className;
    });
    return () => {
      alive = false;
    };
  }, [roomId]);

  /* 기록 저장 (이 브라우저에만) */
  useEffect(() => {
    if (!memberId || !room) return;
    const store: HistStore = { memberId, expiresAt: room.expiresAt, items: hist.slice(-HIST_MAX) };
    try {
      window.localStorage.setItem(histKey(roomId), JSON.stringify(store));
    } catch {
      /* ignore */
    }
  }, [hist, memberId, room, roomId]);

  const upsert = useCallback((it: CommItem) => {
    setHist((prev) => {
      const i = prev.findIndex((x) => x.id === it.id);
      if (i >= 0) {
        const next = prev.slice();
        next[i] = { ...next[i], ...it };
        return next;
      }
      return [...prev, it];
    });
  }, []);

  /* 소켓은 방 정보를 받은 뒤 한 번만 연결 — 제목(className) 변경으로 room 객체가 바뀌어도 다시 연결하지 않음 */
  const roomReady = !!room;
  useEffect(() => {
    if (!roomReady) return undefined;
    const sock = openCommSocket({
      roomId,
      role: 'student',
      onStatus: (s, d) => {
        setStatus(s);
        setStatusDetail(d ?? '');
        if (s === 'ended') {
          setEndReason(d ?? '');
          clearLocal(roomId);
          setOpenId(null);
          setPhase('ended');
        }
      },
      onFrame: (f) => {
        switch (f.t) {
          case 'hello': {
            if (typeof f.rosterMode === 'boolean') setRosterMode(f.rosterMode);
            const hr = f.room as { className?: unknown; pres?: unknown } | undefined;
            const hp = typeof hr?.pres === 'string' ? hr.pres : undefined;
            setRoom((prev) => (prev && prev.pres !== hp ? { ...prev, pres: hp } : prev));
            if (typeof hr?.className === 'string' && hr.className) {
              const hn = hr.className;
              setRoom((prev) => (prev && prev.className !== hn ? { ...prev, className: hn } : prev));
              document.title = hn;
            }
            const c = loadCreds(roomId);
            if (c?.token) sock.send({ t: 'resume', memberId: c.memberId, token: c.token });
            else setPhase('join');
            return;
          }
          case 'room': {
            const nr = f.room as { className?: unknown; pres?: unknown } | undefined;
            const np = typeof nr?.pres === 'string' ? nr.pres : undefined;
            setRoom((prev) => (prev && prev.pres !== np ? { ...prev, pres: np } : prev));
            const name = typeof nr?.className === 'string' ? nr.className : '';
            if (name) {
              setRoom((prev) => (prev && prev.className !== name ? { ...prev, className: name } : prev));
              document.title = name;
            }
            return;
          }
          case 'roster_mode': {
            setRosterMode(Boolean(f.rosterMode));
            return;
          }
          case 'joined': {
            const c = loadCreds(roomId);
            const token = typeof f.token === 'string' ? f.token : c?.token ?? '';
            const fNo = Number(f.number);
            const creds: Creds = {
              memberId: String(f.memberId),
              token,
              nickname: String(f.nickname),
              ...(Number.isInteger(fNo) && fNo > 0 ? { number: fNo } : c?.number ? { number: c.number } : {}),
            };
            window.localStorage.setItem(credsKey(roomId), JSON.stringify(creds));
            setNickname(creds.nickname);
            setMemberId(creds.memberId);
            setJoining(false);
            setJoinErr('');
            setPhase('in');
            /* 서버는 열린 항목만 보냄 → 로컬 기록과 합치고, 목록에 없는 건 종료로 표시 */
            const open = (f.items as CommItem[]) ?? [];
            const mine = (f.myAnswers as Record<string, string>) ?? {};
            /* 입장(페이지 로드) 때마다 지난 기록은 보이지 않음 — 지금 열려 있는 것 + 이후 받은 것만 */
            /* (재연결 시엔 이번 접속에서 받은 것만 유지) */
            const openIds = new Set(open.map((x) => x.id));
            const merged: Hist[] = histRef.current.map((x) => (openIds.has(x.id) ? x : { ...x, closed: true }));
            let newest: Hist | null = null;
            for (const it of open) {
              const i = merged.findIndex((x) => x.id === it.id);
              const base: Hist = i >= 0 ? { ...merged[i], ...it } : { ...it };
              if (mine[it.id] != null) base.answer = mine[it.id];
              if (i >= 0) merged[i] = base;
              else {
                merged.push(base);
                newest = base;
              }
            }
            merged.sort((a, b) => a.at - b.at);
            setHist(merged);
            if (newest && !(newest.kind === 'question' && newest.answer != null)) setOpenId(newest.id);
            return;
          }
          case 'caption': {
            const id = String(f.id ?? '');
            const text = String(f.text ?? '');
            if (!id || !text) return;
            setLiveCaption({ id, text });
            setOpenId((cur) => (cur && cur !== id ? null : cur));
            return;
          }
          case 'item': {
            const it = f.item as CommItem;
            upsert(it);
            setEditing(false);
            if (it.kind === 'caption') setLiveCaption(null);
            setOpenId(it.id);
            return;
          }
          case 'close': {
            const id = String(f.id);
            setHist((prev) => prev.map((x) => (x.id === id ? { ...x, closed: true } : x)));
            setLiveCaption((c) => (c?.id === id ? null : c));
            /* 자막은 닫히면 블록도 내림 */
            setHist((prev) => {
              if (prev.some((x) => x.id === id && x.kind === 'caption')) setOpenId((cur) => (cur === id ? null : cur));
              return prev;
            });
            return;
          }
          case 'answer_ok': {
            const p = pendingAnswerRef.current;
            if (p && p.id === String(f.requestId)) {
              setHist((prev) => prev.map((x) => (x.id === p.id ? { ...x, answer: p.content, answeredAt: Date.now(), seen: true } : x)));
            }
            setEditing(false);
            answerWaitRef.current?.(true);
            answerWaitRef.current = null;
            return;
          }
          case 'error': {
            const codeStr = String(f.code);
            if (codeStr === 'reentry_required') {
              /* 선생님이 내 번호의 입장번호를 바꿈 → 새 입장번호로 다시 입장(기록·번호는 유지) */
              const c = loadCreds(roomId);
              if (c) window.localStorage.setItem(credsKey(roomId), JSON.stringify({ ...c, token: '' }));
              if (typeof f.rosterMode === 'boolean') setRosterMode(f.rosterMode);
              setCode('');
              setJoining(false);
              setJoinErr('입장번호가 바뀌었어요. 선생님께 받은 새 입장번호로 다시 들어와 주세요.');
              setPhase('join');
              return;
            }
            if (codeStr === 'resume_failed') {
              clearLocal(roomId);
              setHist([]);
              setPhase('join');
              return;
            }
            if (codeStr === 'question_closed') {
              const rid = String(f.requestId ?? '');
              setHist((prev) => prev.map((x) => (x.id === rid ? { ...x, closed: true } : x)));
              answerWaitRef.current?.(false);
              answerWaitRef.current = null;
              return;
            }
            setJoining(false);
            let msg = JOIN_ERR[codeStr] ?? '';
            if (codeStr === 'wrong_code' && typeof f.attemptsLeft === 'number') msg += ` (남은 시도 ${f.attemptsLeft}번)`;
            if (msg) setJoinErr(msg);
            return;
          }
          default:
        }
      },
    });
    sockRef.current = sock;
    return () => {
      sock.close();
      sockRef.current = null;
    };
  }, [roomReady, roomId, upsert]);

  /* 만료 시각이 지나면 정리 */
  const roomExpiresAt = room?.expiresAt;
  useEffect(() => {
    if (roomExpiresAt == null) return undefined;
    const t = window.setTimeout(() => {
      /* 사용 시간 끝 → 재연결 시도 중단 */
      sockRef.current?.close();
      setEndReason('expired');
      clearLocal(roomId);
      setOpenId(null);
      setPhase('ended');
    }, Math.max(1000, roomExpiresAt - Date.now()));
    return () => window.clearTimeout(t);
  }, [roomExpiresAt, roomId]);

  /* 항상 맨 아래(최신)로 */
  useLayoutEffect(() => {
    if (phase !== 'in') return;
    listEndRef.current?.scrollIntoView({ block: 'end' });
  }, [hist, openId, phase]);

  /* 질문은 지금 열린 수업 프레젠에서 보낸 것만 (기록·답은 그대로 두고 표시만 거름) */
  const curPres = room?.pres ?? '';
  const shownHist = curPres ? hist.filter((x) => x.kind !== 'question' || x.pres === curPres) : hist;
  const open = shownHist.find((x) => x.id === openId) ?? null;

  const closePopup = useCallback(() => {
    if (openId) setHist((prev) => prev.map((x) => (x.id === openId ? { ...x, seen: true } : x)));
    setOpenId(null);
    setEditing(false);
  }, [openId]);

  /* 공지 자동 숨김(설정된 경우) — 기록에는 남음 */
  useEffect(() => {
    if (!open || open.kind !== 'notice' || !open.durationSec) return undefined;
    const left = open.at + open.durationSec * 1000 - Date.now();
    const t = window.setTimeout(closePopup, Math.max(3000, left));
    return () => window.clearTimeout(t);
  }, [open, closePopup]);

  const join = () => {
    setJoinErr('');
    const values = { number: studentNo, nickname, code };
    const invalid = validateMemberEntry(values, rosterMode);
    if (invalid) {
      setJoinErr(invalid);
      return;
    }
    const frame = buildMemberJoinFrame(values, rosterMode);
    if (!sockRef.current?.send(frame)) {
      setJoinErr('연결이 끊겼어요. 위의 ‘다시 연결’을 눌러 주세요.');
      return;
    }
    setJoining(true);
  };

  const submitAnswer = (content: string) =>
    new Promise<boolean>((resolve) => {
      const q = open;
      const text = content.trim();
      if (!q || q.kind !== 'question' || q.closed || !text) return resolve(false);
      const picked = q.questionType === 'choice' ? choiceAnswerPickedIndexes(text, q.choices ?? []) : [];
      const ci = picked.length === 1 ? picked[0] : -1;
      pendingAnswerRef.current = { id: q.id, content: text };
      const sent = sockRef.current?.send({
        t: 'answer',
        requestId: q.id,
        content: text,
        ...(ci >= 0 ? { choiceIndex: ci } : {}),
        ...(picked.length > 1 ? { choiceIndexes: picked } : {}),
      });
      if (!sent) return resolve(false);
      answerWaitRef.current = resolve;
      window.setTimeout(() => {
        if (answerWaitRef.current === resolve) {
          answerWaitRef.current = null;
          resolve(false);
        }
      }, 6000);
    });


  const tapCard = (x: Hist) => {
    // 열린 질문: 큰 팝업으로 바로 열기 · 공지/종료된 질문은 ‘크게 보기’ 버튼으로
    if (x.kind === 'question' && !x.closed) setOpenId(x.id);
    else if (x.kind === 'caption') {
      setLiveCaption(null);
      setOpenId(x.id);
    }
  };

  const statusOf = (x: Hist) =>
    x.kind === 'question'
      ? x.answer != null
        ? '답변함'
        : x.closed
          ? '종료'
          : '답변 전'
      : x.kind === 'caption'
        ? ''
        : x.seen
          ? '확인함'
          : '새 공지';
  /* 방 사용 시간이 끝났거나(서버 'expired'/'not_found', 시계상 만료) 방이 없음 → 재연결 대신 안내 */
  const roomExpired =
    phase === 'missing' ||
    (phase === 'ended' && endReason !== 'ended') ||
    (roomExpiresAt != null && Date.now() >= roomExpiresAt);
  const showReconnect = !roomExpired && (status === 'disconnected' || (status === 'error' && phase !== 'ended'));

  const qView: RemoteQuestionView | null =
    open && open.kind === 'question'
      ? {
          requestId: open.id,
          text: open.text,
          fromSessionId: '',
          submitted: open.answer != null && !editing,
          questionType: open.questionType ?? 'short',
          choices: open.choices,
          multiSelect: open.questionType === 'choice' && !!open.multiSelect,
          mode: 'submit',
          submittedAnswer: open.answer,
        }
      : null;

  return (
    <div className={`cp-comm-student ${REMOTE_DESIGN_CLASS}`}>
      <header className="cp-topnav cp-comm-topnav" role="banner">
        <div className="cp-topnav-left">
          <span className="cp-logo" aria-label="클래스 플래너">
            <BrandLogo className="cp-brand-logo--nav" />
          </span>
        </div>
        <div className="cp-comm-topnav-center" title={room ? `${room.className}${room.teacherName ? ` (${room.teacherName})` : ''}` : undefined}>
          {room ? (
            <>
              {room.className}
              {room.teacherName ? <span className="cp-comm-topnav-owner"> ({room.teacherName})</span> : null}
            </>
          ) : (
            '개별 디바이스 프레젠테이션'
          )}
        </div>
        <div className="cp-topnav-right">
          {phase === 'in' && nickname ? <span className="cp-theme-toggle cp-comm-topnav-me">{nickname}</span> : null}
        </div>
      </header>

      <main className="cp-comm-student-main">
        {showReconnect ? (
          <TobiNotice tone="warn" text={statusDetail === 'replaced' ? '다른 창에서 접속했어요.' : '연결이 끊겼어요. ‘다시 연결’을 눌러 주세요.'}>
            <button type="button" className="cp-comm-tobi-btn" onClick={() => sockRef.current?.reconnect()}>
              다시 연결
            </button>
          </TobiNotice>
        ) : null}
        {phase === 'loading' ? <TobiNotice text="불러오는 중…" /> : null}
        {roomExpired ? <TobiNotice tone="warn" text="방 사용 시간이 끝났어요. 선생님께 새 QR을 받아 주세요." /> : null}
        {phase === 'ended' && !roomExpired ? <TobiNotice text="개별 디바이스 프레젠테이션이 끝났어요. 참여해 줘서 고마워요!" /> : null}

        {phase === 'join' ? (
          <div className="cp-comm-gate">
            {joinErr ? <TobiNotice compact tone="warn" text={joinErr} /> : null}
            <MemberEntryForm
              title="개별 디바이스 프레젠테이션 입장"
              rosterMode={rosterMode}
              values={{ number: studentNo, nickname, code }}
              onChange={(v) => {
                setStudentNo(v.number);
                setNickname(v.nickname);
                setCode(v.code);
              }}
              onSubmit={join}
              error=""
              busy={joining}
            />
          </div>
        ) : null}

        {phase === 'in' ? (
          shownHist.length === 0 ? (
            <TobiNotice text="선생님이 공지나 질문을 보내면 여기에 나타나요." />
          ) : (
            <ol className="cp-comm-feed" aria-label="공지·질문과 내 답">
              {shownHist.map((x) => {
                const st = statusOf(x);
                const stCls = st === '답변 전' || st === '새 공지' ? 'is-pending' : st === '종료' ? 'is-closed' : 'is-done';
                const qt = x.kind === 'question' ? (x.questionType ?? 'short') : null;
                const hasResp = x.kind === 'question' && x.answer != null;
                const isLive = x.kind === 'question' && !x.closed && !hasResp;
                const collapsed = !isLive && x.kind !== 'caption' && (x.kind === 'notice' ? !!x.seen : hasResp || !!x.closed);
                return (
                  <li
                    key={x.id}
                    className={`cp-comm-row is-in is-${x.kind}${hasResp ? ' has-resp' : ''}${
                      x.kind === 'question' && !x.closed ? ' is-active' : ''
                    }${isLive ? ' is-live' : ''}${collapsed ? ' is-collapsed' : ''}`}
                  >
                    <button
                      type="button"
                      className={`cp-comm-card is-in is-${x.kind}`}
                      onClick={() => tapCard(x)}
                    >
                      <span className="cp-comm-card-head">
                        <span className="cp-comm-card-type">
                          {x.kind === 'notice' ? <NoticeIcon /> : x.kind === 'caption' ? null : <QuestionIcon />}
                          <span className="cp-comm-card-type-label">{x.kind === 'notice' ? '공지' : x.kind === 'caption' ? '자막' : '질문'}</span>
                          {x.kind === 'question' && commSourceLabel(x.src) ? (
                            <span className="cp-comm-card-src">· {commSourceLabel(x.src)}</span>
                          ) : null}
                        </span>
                        <span className="cp-comm-card-meta">
                          <time>{hhmm(x.at)}</time>
                          <span className={`cp-comm-card-status ${stCls}`}>{st}</span>
                        </span>
                      </span>
                      <span className="cp-comm-card-text">{x.text}</span>
                      {qt === 'ox' ? (
                        <span className="cp-remote-ov-ox cp-comm-card-ox" aria-hidden>
                          {(['O', 'X'] as const).map((k) => (
                            <span
                              key={k}
                              className={`cp-remote-ov-ox-btn cp-remote-ov-ox-btn--${k === 'O' ? 'o' : 'x'}${x.answer === k ? ' is-on' : ''}`}
                            >
                              <OxMark kind={k} />
                            </span>
                          ))}
                        </span>
                      ) : null}
                      {qt === 'choice' && x.choices?.length ? (
                        <span className={`cp-remote-ov-choices cp-comm-card-choices${x.choices.length > 4 ? ' is-many' : ''}`} aria-hidden>
                          {x.choices.map((c, i) => (
                            <span key={i} className={`cp-remote-ov-choice${choiceAnswerPickedIndexes(x.answer, x.choices ?? []).includes(i) ? ' is-on' : ''}`}>
                              <span className="cp-remote-ov-choice-num cp-choice-swatch" style={communicationChoiceSwatchStyle(i)}>{i + 1}</span>
                              <span className="cp-remote-ov-choice-text">{c}</span>
                            </span>
                          ))}
                        </span>
                      ) : null}
                      {collapsed && hasResp ? (
                        <span className="cp-comm-sum">
                          내 답{' '}
                          {qt === 'ox' && (x.answer === 'O' || x.answer === 'X') ? <OxMark kind={x.answer} /> : <b>{x.answer}</b>}
                          {!x.closed ? <em>수정하기 ›</em> : null}
                        </span>
                      ) : null}
                      {isLive ? <span className="cp-comm-answer-btn">답하기</span> : null}
                    </button>
                    <button
                      type="button"
                      className="cp-comm-card-expand"
                      aria-label="크게 보기"
                      title="크게 보기"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenId(x.id);
                      }}
                    >
                      <ExpandIcon />
                    </button>
                    {hasResp && !collapsed ? (
                      <div
                        className="cp-comm-resp"
                        aria-label="내 답"
                        role={x.kind === 'question' && !x.closed ? 'button' : undefined}
                        tabIndex={x.kind === 'question' && !x.closed ? 0 : undefined}
                        onClick={() => tapCard(x)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            tapCard(x);
                          }
                        }}
                      >
                        <div className="cp-comm-resp-tile">
                          <span className="cp-comm-resp-by">내 답</span>
                          <span className="cp-comm-resp-text">{x.answer}</span>
                          <time className="cp-comm-resp-time">{hhmm(Math.max(x.answeredAt ?? x.at, x.at))}</time>
                        </div>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ol>
          )
        ) : null}
        <div ref={listEndRef} className="cp-comm-hist-end" aria-hidden />
      </main>



      {phase === 'in' && (liveCaption || open?.kind === 'caption') && !(open && open.kind !== 'caption') ? (
        <RemoteCaptionOverlay
          text={liveCaption?.text ?? open?.text ?? ''}
          onClose={() => {
            setLiveCaption(null);
            if (open?.kind === 'caption') closePopup();
          }}
        />
      ) : null}
      {phase === 'in' && open && open.kind === 'notice' ? (
        <RemoteNoticeOverlay
          backdrop={false}
          notice={{ id: open.id, text: open.text, durationSec: open.durationSec ?? null, shownAt: open.at }}
          review={!!open.seen}
          onClose={closePopup}
          onConfirm={closePopup}
          onBackdropClose={closePopup}
        />
      ) : null}
      {phase === 'in' && open && qView ? (
        <RemoteQuestionOverlay
          backdrop
          question={qView}
          sourceLabel={commSourceLabel(open.src)}
          readOnly={!!open.closed}
          onSubmit={submitAnswer}
          answerAgainLabel="수정 답변 제출"
          onAnswerAgain={() => {
            if (!open.closed) setEditing(true);
          }}
          onDismiss={closePopup}
          onBackdropClose={closePopup}
        />
      ) : null}
    </div>
  );
};

export default CommStudentPage;
